'use client';

import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import type { Tables } from '@/types_db';
import { getStripe } from '@/utils/stripe/client';
import { checkoutWithStripe } from '@/utils/stripe/server';
import { getErrorRedirect } from '@/utils/helpers';
import { User } from '@supabase/supabase-js';
import cn from 'classnames';
import { useRouter, usePathname } from 'next/navigation';
import { useState } from 'react';
import { Offer } from '@/types/offer';

type Subscription = Tables<'subscriptions'>;
type Product = Tables<'products'>;
type Price = Tables<'prices'>;

interface ProductWithPrices extends Product {
  prices: Price[];
}
interface PriceWithProduct extends Price {
  products: Product | null;
}
interface SubscriptionWithProduct extends Subscription {
  prices: PriceWithProduct | null;
}

interface Props {
  user: User | null | undefined;
  products: ProductWithPrices[];
  subscriptions: SubscriptionWithProduct[] | undefined;
  offers: Offer[];
}

type BillingInterval = 'lifetime' | 'year' | 'month' | 'day';

export default function Pricing({ user, products, subscriptions, offers }: Props) {

  const t = useTranslations();
  const router = useRouter();

  const intervals = Array.from(
    new Set(
      products.flatMap((product) =>
        product?.prices?.map((price) => price?.interval)
      )
    )
  );
  
  const [billingInterval, setBillingInterval] =
    useState<BillingInterval>('month');
  const [priceIdLoading, setPriceIdLoading] = useState<string>();
  const currentPath = usePathname();

  const handleStripeCheckout = async (price: Price) => {

    if (user && (subscriptions?.length ?? 0) > 0) {
      return router.push('/account');
    }

    setPriceIdLoading(price.id);

    if (!user) {
      setPriceIdLoading(undefined);
      return router.push('/signin/signup');
    }

    const { errorRedirect, sessionId } = await checkoutWithStripe(
      price,
      currentPath
    );

    if (errorRedirect) {
      setPriceIdLoading(undefined);
      return router.push(errorRedirect);
    }

    if (!sessionId) {
      setPriceIdLoading(undefined);
      return router.push(
        getErrorRedirect(
          currentPath,
          'An unknown error occurred.',
          'Please try again later or contact a system administrator.'
        )
      );
    }

    const stripe = await getStripe();
    stripe?.redirectToCheckout({ sessionId });

    setPriceIdLoading(undefined);
  };

  if (!products.length) {
    return (
      <section className="">
        <div className="max-w-6xl px-4 py-8 mx-auto sm:py-24 sm:px-6 lg:px-8">
          <div className="sm:flex sm:flex-col sm:align-center"></div>
          <p className="text-4xl font-extrabold text-primary sm:text-center sm:text-6xl">
            No subscription pricing plans found. Create them in your{' '}
            <a
              className="text-secondary underline"
              href="https://dashboard.stripe.com/products"
              rel="noopener noreferrer"
              target="_blank"
            >
              Stripe Dashboard
            </a>
            .
          </p>
        </div>
        
      </section>
    );
  } else {
    return (
      <section className="">
        <div className="max-w-6xl px-4 py-8 mx-auto sm:py-24 sm:px-6 lg:px-8">
          <div className="sm:flex sm:flex-col sm:align-center">
            <h1 className="text-4xl font-extrabold text-primary sm:text-center sm:text-6xl">
              Offres Assistant Coach Business
            </h1>
            <p className="max-w-2xl m-auto mt-5 text-xl text-primary sm:text-center sm:text-2xl">
            Libérez votre potentiel entrepreneurial avec un coach IA dédié à votre succès : conseils stratégiques, solutions personnalisées et accompagnement quotidien pour booster votre business et atteindre vos objectifs !
            </p>
            <div className="relative self-center mt-6 bg-primary rounded-lg p-0.5 flex sm:mt-8 border border-zinc-800">
              {intervals.includes('day') && (
                <button
                  onClick={() => setBillingInterval('day')}
                  type="button"
                  className={`${
                    billingInterval === 'day'
                      ? 'relative w-1/2 bg-[#171775] border-zinc-800 shadow-sm text-white'
                      : 'ml-0.5 relative w-1/2 border border-transparent text-zinc-400'
                  } rounded-md m-1 py-2 text-sm font-medium whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-opacity-50 focus:z-10 sm:w-auto sm:px-8`}
                >
                  Quotidien
                </button>
              )}
              {intervals.includes('month') && (
                <button
                  onClick={() => setBillingInterval('month')}
                  type="button"
                  className={`${
                    billingInterval === 'month'
                      ? 'relative w-1/2 bg-[#171775] border-zinc-800 shadow-sm text-white'
                      : 'ml-0.5 relative w-1/2 border border-transparent text-zinc-400'
                  } rounded-md m-1 py-2 text-sm font-medium whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-opacity-50 focus:z-10 sm:w-auto sm:px-8`}
                >
                  Mensuel
                </button>
              )}
              {intervals.includes('year') && (
                <button
                  onClick={() => setBillingInterval('year')}
                  type="button"
                  className={`${
                    billingInterval === 'year'
                      ? 'relative w-1/2 bg-[#171775] border-zinc-800 shadow-sm text-white'
                      : 'ml-0.5 relative w-1/2 border border-transparent text-zinc-400'
                  } rounded-md m-1 py-2 text-sm font-medium whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-opacity-50 focus:z-10 sm:w-auto sm:px-8`}
                >
                  Annuel
                </button>
              )}
            </div>
          </div>
          <div className="mt-12 space-y-0 sm:mt-16 flex flex-wrap justify-center gap-6 lg:max-w-4xl lg:mx-auto xl:max-w-none xl:mx-0">
            {products.map((product) => {
              const price = product?.prices?.find(
                (price) => price.interval === billingInterval
              );
              const offer = offers?.find( 
                (offer) => offer.product_id === product.id
              );
              if (!price) return null;
              const priceString = (price?.unit_amount || 0) > 0 ? new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: price.currency!,
                minimumFractionDigits: 0
              }).format((price?.unit_amount || 0) / 100) : 'Gratuit';
              return (
                <div
                  key={product.id}
                  className={cn(
                    'flex flex-col rounded-lg shadow-sm divide-y divide-zinc-600 bg-[#171775]',
                    {
                      'border-4 border-secondary': subscriptions?.some(subscription => product.id === subscription?.prices?.products?.id)
                    },
                    'flex-1', // This makes the flex item grow to fill the space
                    'basis-1/3', // Assuming you want each card to take up roughly a third of the container's width
                    'max-w-xs' // Sets a maximum width to the cards to prevent them from getting too large
                  )}
                >
                  <div className="p-6">
                    <h2 className="text-2xl font-semibold leading-6 text-white">
                      {product.name}
                    </h2>
                    
                    <p className="mt-8">
                      <span className="text-5xl font-extrabold text-white">
                        {priceString}
                      </span>
                      { (price?.unit_amount || 0) > 0 && <span className="text-base font-medium text-white">
                        /{t(`billingInterval.${billingInterval}`)}
                      </span>}
                    </p>
                    <Button
                      variant="default"
                      type="button"
                      loading={priceIdLoading === price.id}
                      onClick={() => handleStripeCheckout(price)}
                      className="block w-full py-2 mt-8 text-sm font-semibold text-center text-white rounded-md hover:bg-[#171775]"
                    >
                      {(subscriptions?.length ?? 0) > 0 ? 'Gérer' : 'S\'abonner'}
                    </Button>
                    {offer && <div className="flex flex-col gap-2 mt-4 text-white">
                        <div><strong>Nombre de projets : </strong>{(offer.project_limit || 0) >= 0 ? (offer.project_limit || 0) > 1 ? `Jusqu'à ${offer.project_limit} projets` : (offer.project_limit) : 'Illimité'}</div>
                        <div><strong>Thèmes : </strong>{offer.additional_themes === -1 ? 'Tous les thèmes disponibles' : `${offer.default_theme?.name}${ offer.additional_themes > 0 ? ' + '+ offer.additional_themes+' thème au choix' : ''}`}</div>
                        <div><strong>Requêtes IA / Mois : </strong>{(offer.monthly_message_limit || 0) >= 0 ? offer.monthly_message_limit : 'Illimité'}</div>
                      </div>}
                    <p className="mt-4 text-secondary">{product.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
          
        </div>
      </section>
    );
  }
}
